// Product (Ordre 19): the business words of a feedback / bug / request and the small pure rules the screens share. UI only — the server
// checks every right, every move and every link again.
import { formatInstant, inputToIso, isoToInput } from "./time";


export const PRODUCT_READ_KEYS = ["product:view"];
export const PRODUCT_CREATE_KEYS = ["product:submit_feedback", "product:triage"];
export const PRODUCT_KEYS = ["product:view", "product:submit_feedback", "product:triage", "product:manage_roadmap", "product:manage_releases", "product:manage_incidents"];

export const TYPE_LABELS = {
  bug: "Anomalie",
  improvement: "Amélioration",
  feature_request: "Nouvelle fonctionnalité",
  tech_debt: "Dette technique",
  security: "Sécurité",
  ux: "Ergonomie",
};

// Where the feedback comes from. The tickets linked to an item carry the detail.
export const SOURCE_LABELS = {
  support: "Support client",
  commercial: "Équipe commerciale",
  field: "Intervention terrain",
  customer: "Retour direct d'un client",
  internal: "Équipe interne",
};

export const STATUS_LABELS = {
  proposed: "Proposé",
  to_study: "À étudier",
  retained: "Retenu",
  planned: "Planifié",
  in_development: "En développement",
  in_test: "En test",
  delivered: "Livré",
  abandoned: "Abandonné",
};
export const STATUSES = Object.keys(STATUS_LABELS);
export const OPEN_STATUSES = ["proposed", "to_study", "retained", "planned", "in_development", "in_test"];
// The columns of the roadmap « par état », left to right.
export const ROADMAP_COLUMNS = ["retained", "planned", "in_development", "in_test", "delivered"];

export const CUSTOMER_IMPACT_LABELS = { none: "Aucun", low: "Faible", high: "Fort", blocking: "Bloquant" };
export const SEVERITY_LABELS = { minor: "Mineure", major: "Majeure", critical: "Critique" };
export const FREQUENCY_LABELS = { rare: "Rare", regular: "Régulière", frequent: "Fréquente" };
export const EFFORT_LABELS = { s: "S · petit", m: "M · moyen", l: "L · gros", xl: "XL · très gros" };
export const RISK_LABELS = { low: "Faible", medium: "Moyen", high: "Élevé" };
export const PRIORITY_LABELS = { critical: "Critique", high: "Haute", normal: "Normale", low: "Basse" };
export const PRIORITIES = Object.keys(PRIORITY_LABELS);

export const QUALIFICATION_FIELDS = [
  { id: "customer_impact", label: "Impact client", options: CUSTOMER_IMPACT_LABELS },
  { id: "severity", label: "Gravité", options: SEVERITY_LABELS },
  { id: "frequency", label: "Fréquence", options: FREQUENCY_LABELS },
  { id: "effort", label: "Effort estimé", options: EFFORT_LABELS },
  { id: "risk", label: "Risque", options: RISK_LABELS },
  { id: "priority", label: "Priorité", options: PRIORITY_LABELS },
];

export const GITHUB_KIND_LABELS = { issue: "Issue", pull: "Pull request", commit: "Commit", release: "Release", repo: "Dépôt" };
export const GITHUB_STATE_LABELS = { open: "Ouverte", closed: "Fermée", merged: "Fusionnée", published: "Publiée" };

const TONES = {
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  blue: "bg-sky-50 text-sky-800 border-sky-200",
  violet: "bg-violet-50 text-violet-800 border-violet-200",
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
  red: "bg-red-50 text-red-800 border-red-200",
};
const STATUS_TONES = {
  proposed: TONES.slate, to_study: TONES.blue, retained: TONES.violet, planned: TONES.violet, in_development: TONES.amber,
  in_test: TONES.amber, delivered: TONES.emerald, abandoned: TONES.slate,
};
const PRIORITY_TONES = { critical: TONES.red, high: TONES.amber, normal: TONES.blue, low: TONES.slate };
export const statusTone = (status) => STATUS_TONES[status] || TONES.slate;
export const priorityTone = (priority) => PRIORITY_TONES[priority] || TONES.slate;
export const typeTone = (type) => (type === "security" ? TONES.red : type === "bug" ? TONES.amber : TONES.slate);

// The views a person reaches in one tap. `params` are list filters; `permission` hides the view from those it would not help.
export const QUICK_VIEWS = [
  { id: "triage", label: "À qualifier", params: { status: "proposed" }, permission: "product:triage" },
  { id: "mine", label: "Mes éléments", params: { owner: "me" } },
  { id: "test", label: "En test", params: { status: "in_test" } },
  { id: "delivered", label: "Livrés", params: { state: "delivered" } },
];

// --- Moves ---------------------------------------------------------------------------------------------------------------------------
const FORWARD_LABELS = {
  to_study: "Marquer à étudier", retained: "Retenir", planned: "Planifier", in_development: "Lancer le développement",
  in_test: "Passer en test", delivered: "Marquer livré", abandoned: "Abandonner",
};

// The server sends the moves it allows (`item.moves`); this adds the words and what the dialog must ask for.
export function describeMove(item, move) {
  const backward = !!STATUSES.includes(move.status) && isBackward(item.status, move.status);
  const label = backward ? `Revenir à « ${STATUS_LABELS[move.status]} »` : FORWARD_LABELS[move.status] || STATUS_LABELS[move.status];
  const asks = [];
  if (move.needs_reason) asks.push("reason");
  if (move.needs_owner) asks.push("owner");
  return { ...move, backward, label, asks };
}

const BACKWARD = { to_study: "proposed", retained: "to_study", planned: "retained", in_development: "planned", in_test: "in_development", abandoned: "proposed" };
export const isBackward = (from, to) => BACKWARD[from] === to;

// The one move worth a button of its own: the first forward step the person may take (never « abandonner », never a step back).
export function primaryMove(item) {
  const moves = (item.moves || []).filter((m) => m.allowed && !isBackward(item.status, m.status) && m.status !== "abandoned");
  const order = STATUSES;
  moves.sort((a, b) => order.indexOf(a.status) - order.indexOf(b.status));
  return moves.length ? describeMove(item, moves[0]) : null;
}

// The other moves, for the « … » menu.
export function otherMoves(item) {
  const primary = primaryMove(item);
  return (item.moves || []).filter((m) => m.allowed && m.status !== primary?.status).map((m) => describeMove(item, m));
}

// --- Words ----------------------------------------------------------------------------------------------------------------------------
export function pharmaciesText(item) {
  const total = item.impacted_pharmacies || 0;
  if (!total) return "";
  const text = `${total} pharmacie${total > 1 ? "s" : ""} touchée${total > 1 ? "s" : ""}`;
  return item.pharmacies_estimate ? `${text} (dont ${item.pharmacies_estimate} estimée${item.pharmacies_estimate > 1 ? "s" : ""})` : text;
}

const FIELD_LABELS = {
  title: "le titre", description: "la description", type: "le type", source: "la source", pharmacies_estimate: "l'estimation des pharmacies",
  theme: "le thème", target_version: "la version cible", owner_id: "le responsable", depends_on: "les dépendances",
  customer_impact: "l'impact client", severity: "la gravité", frequency: "la fréquence", effort: "l'effort", risk: "le risque", priority: "la priorité",
};
const listFields = (fields) => (fields || []).map((f) => FIELD_LABELS[f] || f).join(", ");

// One line of the history, in words.
export function historyText(entry) {
  const data = entry.data || {};
  switch (entry.kind) {
    case "created": return data.reopens_item_id ? "Créé pour reprendre un élément livré" : "Créé";
    case "updated": return `Modifié : ${listFields(data.fields)}`;
    case "qualified": return `Qualifié : ${listFields(Object.keys(data))}`;
    case "status": return `${STATUS_LABELS[data.from] || data.from} → ${STATUS_LABELS[data.to] || data.to}`;
    case "merged": return `Fusionné dans ${data.number || "un autre élément"}`;
    case "absorbed": return `A absorbé ${data.number || "un élément"}`;
    case "unmerged": return "Fusion défaite";
    case "released": return `${data.number || "Un élément"} n'est plus fusionné ici`;
    case "ticket_linked": return `Ticket ${data.number || ""} rattaché`.trim();
    case "ticket_unlinked": return "Ticket détaché";
    case "github_linked": return `Lien GitHub ajouté (${GITHUB_KIND_LABELS[data.kind] || data.kind})`;
    case "github_unlinked": return "Lien GitHub retiré";
    case "release_linked": return `Rattaché à la version ${data.label || data.number || ""}`.trim();
    case "release_unlinked": return `Retiré de la version ${data.number || ""}`.trim();
    case "functional_check": return "Vérifié en conditions réelles";
    default: return entry.kind;
  }
}

// --- Roadmap grouping -----------------------------------------------------------------------------------------------------------------
// by: "status" | "theme" | "version". Groups come out in a stable order, empty ones are dropped (except the status columns, which stay so
// the board keeps its shape).
export function groupRoadmap(items, by) {
  if (by === "status") {
    return ROADMAP_COLUMNS.map((status) => ({ key: status, label: STATUS_LABELS[status], items: items.filter((i) => i.status === status) }));
  }
  const field = by === "theme" ? "theme" : "target_version";
  const empty = by === "theme" ? "Sans thème" : "Sans version cible";
  const groups = new Map();
  for (const item of items) {
    const label = (item[field] || "").trim() || empty;
    const key = label.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
    if (!groups.has(key)) groups.set(key, { key, label, items: [] });
    groups.get(key).items.push(item);
  }
  return [...groups.values()].sort((a, b) => (a.label === empty) - (b.label === empty) || a.label.localeCompare(b.label, "fr", { numeric: true }));
}

export function productErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.msg) return detail.msg;
  if (Array.isArray(detail)) return "Vérifiez les champs saisis.";
  return fallback;
}

export function formatDateTime(value) {
  return formatInstant(value);
}

export function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// A new key per distinct content: the Idempotency-Key only protects against the SAME tap sent twice.
export const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);


// --- Releases (19-3) ---------------------------------------------------------------------------------------------------------------
export const RELEASE_PRODUCT_LABELS = { crm: "Jamora CRM", jamora: "Jamora (pharmacie)" };
export const RELEASE_STATE_LABELS = { planned: "Prévue", delivered: "Livrée" };
// « Confirmée » only when the CRM's running commit is the proof; every other delivered version is « déclarée » (D-30).
export const CONFIRMATION_LABELS = { confirmed: "Confirmée en production", declared: "Déclarée, non confirmée", none: "" };
export const CONFIRMATION_HINTS = {
  confirmed: "La preuve est le commit qui tourne en ce moment.",
  declared: "Déclarée par une personne : la production a pu avancer depuis, ou la version qui tourne n'a pas pu être comparée à sa preuve.",
};

const RELEASE_TONES = { planned: "bg-slate-100 text-slate-700 border-slate-200", delivered: "bg-emerald-50 text-emerald-800 border-emerald-200" };
const CONFIRMATION_TONES = { confirmed: "bg-emerald-50 text-emerald-800 border-emerald-200", declared: "bg-amber-50 text-amber-800 border-amber-200" };
export const releaseTone = (state) => RELEASE_TONES[state] || RELEASE_TONES.planned;
export const confirmationTone = (value) => CONFIRMATION_TONES[value] || "";

// A proof is shown short when it is a SHA (the first 7 characters), whole when it is a tag.
export const proofText = (release) => (release.deploy_ref_kind === "sha" ? (release.deploy_ref || "").slice(0, 7) : release.deploy_ref || "");

// « Livré en 2026.10.3 » for the lists and the ticket's card; several versions read « 2026.10.3, 2026.11 ».
export function deliveredInText(entries) {
  const labels = (entries || []).map((e) => e.label).filter(Boolean);
  return labels.length ? `Livré dans ${labels.join(", ")}` : "";
}

// The day a delivery form starts on (today, in the browser's calendar) and the largest day it accepts.
export function todayInput(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// What a delivery form needs before « Livrer » is enabled: a proof the server will accept, and a day that is not in the future.
export function deliveryProblem({ deployRef, deployedAt }, today = todayInput()) {
  const ref = (deployRef || "").trim();
  if (!ref) return "Indiquez la preuve de déploiement.";
  if (/^[0-9a-fA-F]+$/.test(ref) && (ref.length < 7 || ref.length > 40)) return "Un commit fait 7 à 40 caractères hexadécimaux.";
  if (!/^[A-Za-z0-9._+/-]{1,100}$/.test(ref)) return "La preuve est un commit (7 à 40 caractères hexadécimaux) ou une étiquette git.";
  if (!deployedAt) return "Indiquez le jour du déploiement.";
  if (deployedAt > today) return "Le jour du déploiement ne peut pas être dans le futur.";
  return "";
}


// --- Incidents (19-4) --------------------------------------------------------------------------------------------------------------
export const INCIDENT_SEVERITY_LABELS = { minor: "Mineur", major: "Majeur", critical: "Critique" };
export const INCIDENT_STATE_LABELS = { open: "Ouvert", mitigated: "Contourné", resolved: "Résolu", closed: "Clos" };
// The words of a move, by the state it leads to (a step back is « Rouvrir » / « Revenir à… »).
export const INCIDENT_MOVE_LABELS = { mitigated: "Marquer comme contourné", resolved: "Marquer comme résolu", closed: "Clore l'incident" };
export const INCIDENT_BACK_LABELS = { open: "Remettre « ouvert »", mitigated: "Revenir à « contourné »", resolved: "Revenir à « résolu »" };
export const INCIDENT_STATE_FILTERS = [
  { id: "live", label: "En cours" }, { id: "resolved", label: "À clore" }, { id: "closed", label: "Clos" }, { id: "", label: "Tous" },
];

const SEVERITY_TONES = {
  minor: "bg-slate-100 text-slate-700 border-slate-200",
  major: "bg-amber-50 text-amber-800 border-amber-200",
  critical: "bg-red-50 text-red-800 border-red-200",
};
const INCIDENT_STATE_TONES = {
  open: "bg-red-50 text-red-800 border-red-200",
  mitigated: "bg-amber-50 text-amber-800 border-amber-200",
  resolved: "bg-sky-50 text-sky-800 border-sky-200",
  closed: "bg-slate-100 text-slate-700 border-slate-200",
};
export const severityTone = (value) => SEVERITY_TONES[value] || SEVERITY_TONES.minor;
export const incidentStateTone = (value) => INCIDENT_STATE_TONES[value] || INCIDENT_STATE_TONES.closed;

// 95 → « 1 h 35 », 3000 → « 2 j 2 h », 0 → « moins d'une minute ».
export function formatDuration(minutes) {
  if (minutes == null) return "";
  if (minutes < 1) return "moins d'une minute";
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const rest = minutes % 60;
  if (days) return hours ? `${days} j ${hours} h` : `${days} j`;
  if (hours) return rest ? `${hours} h ${String(rest).padStart(2, "0")}` : `${hours} h`;
  return `${rest} min`;
}

// The value of an <input type="datetime-local"> from an ISO instant, read in the person's own time zone (Profil), and back.
export const toLocalInput = (iso) => isoToInput(iso);
export function fromLocalInput(value) {
  return inputToIso(value) || "";
}

// What the opening form needs before « Ouvrir l'incident » is enabled; the server applies the same rules.
export function incidentFormProblem({ title, startedAt, detectedAt }, now = new Date()) {
  if ((title || "").trim().length < 3) return "Donnez un titre à l'incident (3 caractères au moins).";
  if (!startedAt) return "Indiquez quand l'incident a commencé.";
  const started = new Date(startedAt);
  if (started > now) return "L'incident ne peut pas avoir commencé dans le futur.";
  if (detectedAt && new Date(detectedAt) < started) return "On ne peut pas détecter un incident avant son début.";
  if (detectedAt && new Date(detectedAt) > now) return "La détection ne peut pas être dans le futur.";
  return "";
}

export const POSTMORTEM_FIELDS = [
  { id: "cause", label: "Cause", hint: "Ce qui a réellement provoqué l'incident." },
  { id: "impact", label: "Impact constaté", hint: "Ce que les pharmacies et l'équipe ont vécu, mesuré si possible." },
  { id: "lessons", label: "Enseignements", hint: "Ce qu'on change pour que cela n'arrive plus." },
];
export const postmortemComplete = (pm) => !!pm && POSTMORTEM_FIELDS.every((f) => (pm[f.id] || "").trim());

// The line of the timeline for a system entry; a note keeps its own text.
export function incidentEntryText(entry) {
  const d = entry.data || {};
  switch (d.event) {
    case "opened": return `Incident ouvert (${INCIDENT_SEVERITY_LABELS[d.severity] || d.severity})`;
    case "state": return `${INCIDENT_STATE_LABELS[d.from] || d.from} → ${INCIDENT_STATE_LABELS[d.to] || d.to}`;
    case "updated": return "Fiche modifiée";
    case "postmortem": return d.complete ? "Post-mortem complet" : "Post-mortem enregistré (incomplet)";
    case "ticket_linked": return `Ticket ${d.number || ""} rattaché`.replace("  ", " ");
    case "ticket_unlinked": return "Ticket détaché";
    case "item_linked": return `Retour produit ${d.number || ""} lié`.replace("  ", " ");
    case "item_unlinked": return "Retour produit délié";
    case "task_linked": return "Action préventive liée";
    case "task_unlinked": return "Action préventive déliée";
    default: return "Mise à jour";
  }
}

// The move buttons of the detail: the forward ones first, the step back last (it asks for a reason).
export function incidentMoveLabel(move) {
  return move.backward ? INCIDENT_BACK_LABELS[move.state] : INCIDENT_MOVE_LABELS[move.state];
}

// « En production aujourd'hui » (lot 29, V4): how one product's card reads from `GET /product/versions/current`.
export function ageLabel(value, now = new Date()) {
  const moment = new Date(value);
  if (!value || Number.isNaN(moment.getTime())) return "";
  const days = Math.floor((now.getTime() - moment.getTime()) / 86400000);
  if (days < 0) return "";
  if (days === 0) return "aujourd'hui";
  if (days === 1) return "hier";
  if (days < 60) return `il y a ${days} jours`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

const RUNNING_NOTES = {
  not_configured: "La version de Jamora n'est pas lue depuis le CRM : l'adresse de sa santé n'est pas renseignée.",
  unavailable: "La version de Jamora n'a pas pu être lue pour l'instant.",
  unversioned: "Cette installation n'a pas de numéro de version (construction locale).",
};

export function runningVersionView(entry) {
  if (!entry) return { tone: "slate", badge: "", note: "" };
  if (entry.reading !== "ok") return { tone: "slate", badge: "Non lue", note: RUNNING_NOTES[entry.reading] || RUNNING_NOTES.unavailable };
  if (entry.state === "unrecorded") {
    return { tone: "amber", badge: "Non enregistrée", note: "Cette version tourne, mais aucune version n'est enregistrée dans le CRM pour elle (le déploiement n'a pas pu la signaler)." };
  }
  // A published number (X.Y.Z) runs on every deployment made since its publication: the running commit is usually AHEAD of the release's.
  if (entry.ahead) {
    return { tone: "slate", badge: "Changements non publiés", note: "Cette version est publiée ; le code en production contient des changements plus récents, qui seront publiés avec la prochaine version." };
  }
  return entry.confirmed
    ? { tone: "emerald", badge: "Confirmée en production", note: "" }
    : { tone: "amber", badge: "Enregistrée, commit différent", note: "Une version porte ce numéro, mais avec un autre commit que celui qui tourne." };
}

// A release is « confirmed » when the card above says the running commit is its proof — the same reading for every product, so the list
// and the release page never contradict the cards. Never downgrades what the server already confirmed.
export function confirmedReleaseIds(products) {
  const ids = new Set();
  for (const entry of products || []) if (entry.confirmed && entry.recorded?.id) ids.add(entry.recorded.id);
  return ids;
}

export const releaseConfirmation = (release, confirmedIds) => (confirmedIds && confirmedIds.has(release.id) ? "confirmed" : release.confirmation);
